import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { getStoredAuthRole, isAdminRole, isManagerRole, isTesterOrAdminRole } from '../lib/realm'
import {
  api,
  getModels,
  getChatConversations,
  getChatConversation,
  createTestChat,
  updateTestChat,
  simulateIncomingMessage,
  simulatePurchase,
  purgeChatConversation,
  sendManualConversationMessage,
  setChatManualMode,
  setChatPpvPermission,
  unlockChatSafetyLock,
  getBotMessageLog,
  getBotLogsList,
  getBotLogDetail,
  subscribeChatEvents,
  uploadTestMedia,
  getFanBotProfile,
  getChatSafetyStatus,
  getMyCapabilities,
  getToken,
} from '../api'
import { API_BASE } from '../api/client.js'
import { getMediaFileUrl } from '../api/media.js'
import { listConfigs } from '../api/graphConfigs'
import { L3_SCHEMA } from '../config/graphEntities'
import { Select, Pagination, PageSection, FilterGroup, FiltersRow } from '../components/ui'
import { IconEdit, IconTrash, IconLogs } from '../components/ui/Icons'
import MediaThumbnail from '../components/MediaThumbnail'
import ConfirmModal from '../components/ConfirmModal'
import SafetyLockNotice from '../components/SafetyLockNotice'
import { resumeWithSafetyReview, safetyReviewMessage } from '../lib/safetyReview'
import GraphLogDetail from '../components/GraphLogDetail'
import { formatDurationFromTimestamps, formatInvokedAt } from '../utils/logFormatters'
import Modal from '../components/Modal'
import { useI18n } from '../i18n'
import '../styles/pages/models.css'
import '../styles/pages/page-common.css'
import '../styles/pages/chats.css'

const INTERACTION_STYLE_FIELDS = [
  { key: 'directness', labelKey: 'chats.funnel.style.directness' },
  { key: 'warmth_need', labelKey: 'chats.funnel.style.warmth' },
  { key: 'playfulness', labelKey: 'chats.funnel.style.playfulness' },
  { key: 'detail_preference', labelKey: 'chats.funnel.style.detail' },
  { key: 'pace', labelKey: 'chats.funnel.style.pace' },
]

function normalizeApiPath(url) {
  if (!url || typeof url !== 'string') return null
  // Only handle local/internal URLs
  if (url.startsWith('/api/')) return url.slice(4) // keeps the leading slash, e.g. /uploads/...
  if (url.includes('/api/')) {
    const parts = url.split('/api/')
    return '/' + parts[1] // ensures leading slash
  }
  return url
}

function AuthedImage({ url, alt = '', className }) {
  const [blobUrl, setBlobUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  const blobRef = useRef(null)

  useEffect(() => {
    const path = normalizeApiPath(url)
    if (!path) return

    let revoked = false
    setFailed(false)
    api(path, { skipContentType: true })
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (!blob) throw new Error('blob_null')
        const nextUrl = URL.createObjectURL(blob)
        if (revoked) {
          URL.revokeObjectURL(nextUrl)
          return
        }
        blobRef.current = nextUrl
        setBlobUrl(nextUrl)
      })
      .catch(() => {
        if (!revoked) setFailed(true)
      })

    return () => {
      revoked = true
      const toRevoke = blobRef.current
      if (toRevoke) {
        URL.revokeObjectURL(toRevoke)
        blobRef.current = null
      }
      setBlobUrl(null)
    }
  }, [url])

  if (failed) return <div className={className} />
  if (!blobUrl) return <div className={className} />
  return <img src={blobUrl} alt={alt} className={className} loading="lazy" />
}

function AuthedAudio({ url, className }) {
  const [blobUrl, setBlobUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  const blobRef = useRef(null)

  useEffect(() => {
    const path = normalizeApiPath(url)
    if (!path) return

    let revoked = false
    setFailed(false)
    api(path, { skipContentType: true })
      .then((res) => (res.ok ? res.blob() : null))
      .then((blob) => {
        if (!blob) throw new Error('blob_null')
        const nextUrl = URL.createObjectURL(blob)
        if (revoked) {
          URL.revokeObjectURL(nextUrl)
          return
        }
        blobRef.current = nextUrl
        setBlobUrl(nextUrl)
      })
      .catch(() => {
        if (!revoked) setFailed(true)
      })

    return () => {
      revoked = true
      const toRevoke = blobRef.current
      if (toRevoke) {
        URL.revokeObjectURL(toRevoke)
        blobRef.current = null
      }
      setBlobUrl(null)
    }
  }, [url])

  if (failed) return <div className={className} />
  if (!blobUrl) return <div className={className} />
  return <audio className={className} controls preload="none" src={blobUrl} />
}

/** PPV unlocked: load full file from DB when available, else preview (no error toasts on 404). */
function AuthedBubbleVideo({ modelId, mediaId, className }) {
  const [blobUrl, setBlobUrl] = useState(null)
  const [isVideo, setIsVideo] = useState(false)
  const [failed, setFailed] = useState(false)
  const blobRef = useRef(null)

  useEffect(() => {
    if (!modelId || !mediaId) return undefined

    let revoked = false
    setFailed(false)
    setBlobUrl(null)
    setIsVideo(false)

    const kinds = ['full', 'preview']
    ;(async () => {
      for (const kind of kinds) {
        const path = getMediaFileUrl(modelId, mediaId, kind)
        try {
          const headers = {}
          const tok = getToken()
          if (tok) headers.Authorization = `Bearer ${tok}`
          const res = await fetch(`${API_BASE}${path}`, { headers, cache: 'no-store' })
          if (!res.ok || revoked) continue
          const blob = await res.blob()
          if (revoked) return
          const ct = (blob.type || '').toLowerCase()
          const nextUrl = URL.createObjectURL(blob)
          if (blobRef.current) URL.revokeObjectURL(blobRef.current)
          blobRef.current = nextUrl
          setBlobUrl(nextUrl)
          setIsVideo(ct.startsWith('video/'))
          return
        } catch {
          continue
        }
      }
      if (!revoked) setFailed(true)
    })()

    return () => {
      revoked = true
      const toRevoke = blobRef.current
      if (toRevoke) {
        URL.revokeObjectURL(toRevoke)
        blobRef.current = null
      }
      setBlobUrl(null)
    }
  }, [modelId, mediaId])

  if (failed) {
    return <MediaThumbnail modelId={modelId} mediaId={mediaId} alt="" className={className} />
  }
  if (!blobUrl) return <div className={className} />
  if (isVideo) {
    return (
      <video
        className={className}
        controls
        playsInline
        preload="metadata"
        src={blobUrl}
      />
    )
  }
  return <img src={blobUrl} alt="" className={className} loading="lazy" />
}

function stripHtml(html) {
  if (!html || typeof html !== 'string') return ''
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

/** PPV: first "load" fetches only free; after purchase (or no PPV) fetch all. */
function outMediaModeAfterUserLoadClick(msg) {
  if (!msg || msg.direction !== 'out') return 'full'
  const po = msg.purchase_offer
  if (!po) return 'full'
  if (po.is_purchased || po.is_free_group) return 'full'
  const pr = Number(po.price_int ?? po.price ?? 0)
  const paidL = po.paid_media_ids || []
  const hasPpv = pr > 0 || paidL.length > 0
  if (!hasPpv) return 'full'
  return 'free'
}

function ChatLogExpandableItem({ g, isExpanded, onToggle, logDetail, loadingLog, logError, t, lang }) {
  const duration = formatDurationFromTimestamps(g.created_at, g.completed_at, lang)
  return (
    <li className={`chats-log-expandable-item ${isExpanded ? 'chats-log-expandable-item--open' : ''}`}>
      <button
        type="button"
        className="chats-log-list-item chats-log-list-item--expandable"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className="chats-log-expand-icon">{isExpanded ? '▼' : '▶'}</span>
        <span className="chats-log-expandable-content">
          <span className="chats-log-list-preview">{g.input_preview || t('chats.logs.noPreview')}</span>
          <span className="chats-log-list-meta">
            {formatInvokedAt(g.created_at) || ''}
          </span>
        </span>
        <span className="chats-log-expandable-badges">
          <span className={`chats-log-status-badge chats-log-status-badge--${(g.status || '').toLowerCase()}`} data-status={g.status}>
            {g.status || '—'}
          </span>
          {duration != null && (
            <span className="chats-log-duration-badge">{duration}</span>
          )}
        </span>
      </button>
      {isExpanded && (
        <div className="chats-log-expandable-body">
          {loadingLog ? (
            <p className="chats-log-muted">{t('chats.logs.loading')}</p>
          ) : logError ? (
            <p className="chats-log-error">{logError}</p>
          ) : logDetail ? (
            <GraphLogDetail logData={logDetail} />
          ) : null}
        </div>
      )}
    </li>
  )
}

function MessageBubble({ msg, modelId, selected, onSelect, onBuy, isTestChat, outMediaMode, onLoadOutMedia, t }) {
  const isOut = msg.direction === 'out'
  const canOpenLog = isOut
  const hasPurchaseOffer = isOut && isTestChat && msg.purchase_offer
  const offerPurchased = Boolean(msg.purchase_offer?.is_purchased)
  const text = stripHtml(msg.text || '')
  const time = msg.created_at ? new Date(msg.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : ''
  const price = msg.purchase_offer?.price
  const priceNum = Number(
    msg.purchase_offer?.price_int ?? msg.purchase_offer?.price ?? 0
  )
  const paidList = msg.purchase_offer?.paid_media_ids || []
  const paidMediaCount = Number(msg.purchase_offer?.paid_media_count || 0)
  const isFreeGroup = Boolean(msg.purchase_offer?.is_free_group)
  const paidMediaIds = new Set(paidList.map(Number))
  // Simulate test purchase only when there is something to pay (not pure free stage).
  const showSimulatedBuy =
    hasPurchaseOffer &&
    !offerPurchased &&
    !isFreeGroup &&
    (priceNum > 0 || paidList.length > 0 || paidMediaCount > 0 || msg.purchase_offer?.has_paid_media)
  // If price > 0 and paid_media_ids empty — entire bundle is paid until purchase (legacy).
  // After purchase API clears paid_media_ids; do not treat that as "all locked" (see is_purchased).
  const allPaid =
    hasPurchaseOffer && !offerPurchased && priceNum > 0 && paidMediaIds.size === 0
  const attachments = msg.attachments || []
  const mediaAttachments = attachments.filter((a) => {
    const t = a.media_type || 'photo'
    return t === 'photo' || t === 'video' || t === 'gif'
  })
  const mediaRedacted = Boolean(
    msg.media_redacted ||
    msg.purchase_offer?.media_redacted ||
    mediaAttachments.some((item) => item?.redacted)
  )
  const isLocked = (mediaId) => {
    if (isFreeGroup) return false
    if (offerPurchased) return false
    return allPaid || paidMediaIds.has(Number(mediaId))
  }
  // Lazy ⬇ + DB-backed previews: only for test chats. Real OF traffic uses API media — always show content.
  const mode = isTestChat ? (outMediaMode || 'unloaded') : 'full'
  const shouldLoadBinary = (mediaId) => {
    if (mediaRedacted) return false
    if (mode === 'unloaded') return false
    if (mode === 'free') return !isLocked(mediaId)
    return true
  }
  const outHasModelMedia = isOut && modelId && mediaAttachments.length > 0
  const showOutMediaLoad = outHasModelMedia && !mediaRedacted && mode === 'unloaded' && onLoadOutMedia
  // Incoming media from fan (always load — fan content is from URLs on message, not throttled here)
  const incomingMedia = (!isOut && msg.incoming_media) || []
  const incomingPhotos = incomingMedia.filter(m => m.type === 'photo' || m.type === 'gif')
  const incomingAudios = incomingMedia.filter(m => m.type === 'audio')
  const incomingVideos = incomingMedia.filter(m => m.type === 'video')

  // Copy to clipboard
  const [copyingLog, setCopyingLog] = useState(false)
  const handleCopyMessageLog = async (e) => {
    e.stopPropagation()
    setCopyingLog(true)
    try {
      const { exportMessageLog } = await import('../api/model_data.js')
      const result = await exportMessageLog(msg.id)
      if (result?.text) {
        navigator.clipboard.writeText(result.text)
        alert(t('common.copied') || 'Copied to clipboard')
      }
    } catch (err) {
      console.error('Failed to copy message log:', err)
      alert(t('common.error') || 'Error')
    } finally {
      setCopyingLog(false)
    }
  }

  const mainBubble = (
        <button
          type="button"
          className={`chat-bubble ${isOut ? 'chat-bubble--out' : 'chat-bubble--in'} ${selected ? 'chat-bubble--selected' : ''} ${canOpenLog ? 'chat-bubble--clickable' : ''}`}
          onClick={() => onSelect(msg)}
          title={canOpenLog ? t('chats.logs.openHint') : t('chats.logs.incomingHint')}
        >
          {/* Outgoing — model content (lazy: binary only when mode is free/full for that slot) */}
          {isOut && mediaAttachments.length > 0 && modelId && (
            <div
              className={`chat-bubble-attachments ${mediaAttachments.length > 1 ? 'chat-bubble-attachments--slider' : ''}`}
              title={mediaAttachments.length > 1 ? t('chats.attachments.multiSwipe') : undefined}
            >
              {mediaAttachments.map((a, index) => {
                const locked = isLocked(a.media_id)
                const loadBin = shouldLoadBinary(a.media_id)
                return (
                  <div
                  key={a.media_id || `${a.media_type || 'media'}-${a.position || index}`}
                  className={`chat-bubble-attachment-wrap ${a.media_type === 'video' && !locked && loadBin ? 'chat-bubble-attachment-wrap--video-unlocked' : ''} ${locked && !mediaRedacted ? 'chat-bubble-attachment-wrap--locked' : ''} ${mediaRedacted ? 'chat-bubble-attachment-wrap--redacted' : ''}`}
                >
                  {loadBin ? (
                    a.media_type === 'video' && !locked ? (
                    <AuthedBubbleVideo
                      modelId={modelId}
                      mediaId={a.media_id}
                      className="chat-bubble-attachment"
                    />
                    ) : (
                    <MediaThumbnail
                      modelId={modelId}
                      mediaId={a.media_id}
                      alt=""
                      className="chat-bubble-attachment"
                    />
                    )
                  ) : (
                    <div
                      className="chat-bubble-attachment chat-bubble-attachment--placeholder"
                      aria-hidden
                    >
                      <span>{a.media_type === 'video' ? '🎬' : a.media_type === 'audio' ? '🎤' : a.media_type === 'gif' ? 'GIF' : '🖼'}</span>
                      {mediaRedacted && <small>{t('chats.attachments.redactedItem', { type: a.media_type || 'media' })}</small>}
                    </div>
                  )}
                  {locked && !mediaRedacted && loadBin && <div className="chat-bubble-attachment-blur" aria-hidden />}
                </div>
                )
              })}
            </div>
          )}
          {isOut && mediaRedacted && mediaAttachments.length > 0 && (
            <div className="chat-bubble-media-redacted-note">
              {t('chats.attachments.redactedSummary', { count: mediaAttachments.length })}
            </div>
          )}
          {/* Incoming photos from fan */}
          {!isOut && incomingPhotos.length > 0 && (
            <div className={`chat-bubble-attachments ${incomingPhotos.length > 1 ? 'chat-bubble-attachments--slider' : ''}`}>
              {incomingPhotos.map((m) => (
                <div key={m.id || m.thumb_url} className="chat-bubble-attachment-wrap">
                  {(m.preview_url || m.thumb_url || m.full_url || '').startsWith('/api/') ? (
                    <AuthedImage
                      url={m.preview_url || m.thumb_url || m.full_url}
                      alt=""
                      className="chat-bubble-attachment chat-bubble-attachment--incoming"
                    />
                  ) : (
                    <img
                      src={m.preview_url || m.thumb_url || m.full_url}
                      alt=""
                      className="chat-bubble-attachment chat-bubble-attachment--incoming"
                      loading="lazy"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Incoming video from fan */}
          {!isOut && incomingVideos.length > 0 && (
            <div className="chat-bubble-attachments">
              {incomingVideos.map((m) => (
                <div key={m.id || m.thumb_url} className="chat-bubble-attachment-wrap chat-bubble-attachment-wrap--video">
                  {(m.preview_url || m.thumb_url || '').startsWith('/api/') ? (
                    <AuthedImage
                      url={m.preview_url || m.thumb_url}
                      alt=""
                      className="chat-bubble-attachment chat-bubble-attachment--incoming"
                    />
                  ) : (
                    <img
                      src={m.preview_url || m.thumb_url}
                      alt=""
                      className="chat-bubble-attachment chat-bubble-attachment--incoming"
                      loading="lazy"
                    />
                  )}
                  <span className="chat-bubble-video-icon">▶</span>
                </div>
              ))}
            </div>
          )}
          {/* Incoming audio from fan */}
          {!isOut && incomingAudios.length > 0 && (
            <div className="chat-bubble-audio">
              {incomingAudios.map((m) => (
                <div key={m.id || m.full_url} className="chat-bubble-audio-item">
                  <span className="chat-bubble-audio-icon">🎤</span>
                  <span className="chat-bubble-audio-label">
                    {t('chats.voiceMessage')}
                    {m.duration ? ` (${m.duration}s)` : ''}
                  </span>
                  {(m.full_url || '').startsWith('/api/') ? (
                    <AuthedAudio url={m.full_url} className="chat-bubble-audio-player" />
                  ) : (
                    <audio className="chat-bubble-audio-player" controls preload="none" src={m.full_url} />
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="chat-bubble-text">{text || '—'}</div>
          {offerPurchased && (
            <div className="chat-bubble-purchased" role="status">
              {t('chats.purchase.purchased')}
            </div>
          )}
          <div className="chat-bubble-time">{time}</div>
        </button>
  )

  const inner = (
    <div className="chat-bubble-inner">
      {isOut && outHasModelMedia && isTestChat ? (
        <div className="chat-bubble-out-row">
          {showOutMediaLoad && (
            <button
              type="button"
              className="chat-bubble-media-load"
              onClick={(e) => {
                e.stopPropagation()
                onLoadOutMedia?.(msg)
              }}
              title={t('chats.attachments.loadMediaTitle')}
              aria-label={t('chats.attachments.loadMediaTitle')}
            >
              ⬇
            </button>
          )}
          {mainBubble}
        </div>
      ) : (
        mainBubble
      )}
        {showSimulatedBuy && (
          <button
            type="button"
            className="chat-bubble-buy-btn"
            onClick={() => onBuy(msg)}
          >
            {t('chats.purchase.buy')} {priceNum > 0 ? `$${priceNum}` : price ? `$${price}` : ''}
          </button>
        )}
        {isOut && msg.id && (
          <button
            type="button"
            className="chat-bubble-copy-log-btn"
            onClick={handleCopyMessageLog}
            disabled={copyingLog}
            title={t('chats.logs.copyLog') || 'Copy message log'}
          >
            {copyingLog ? '...' : '📋'}
          </button>
        )}
      </div>
  )

  return (
    <div className={`chat-bubble-wrap ${isOut ? 'chat-bubble-wrap--out' : ''}`}>
      {inner}
    </div>
  )
}

export default function ChatsPage() {
  const { t, lang } = useI18n()
  const role = getStoredAuthRole()
  const isUser = ['manager', 'user', 'agency'].includes(role)
  const isTester = role === 'tester' || role === 'tester_no_media' || role === 'tester_plus'
  const canManage = isManagerRole(role)
  const canAdminister = isAdminRole(role)
  const [canUseDiagnostics, setCanUseDiagnostics] = useState(isTesterOrAdminRole(role))
  const canSwitchTestReal = canManage

  const [models, setModels] = useState([])
  const [modelId, setModelId] = useState(null)
  const [conversations, setConversations] = useState([])
  const [selected, setSelected] = useState(null) // { modelId, fanId }
  const [messages, setMessages] = useState([])
  const [fan, setFan] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingModels, setLoadingModels] = useState(true)
  const [loadingChat, setLoadingChat] = useState(false)
  const [chatsError, setChatsError] = useState(null)
  const [inputError, setInputError] = useState(null)
  const [confirmAction, setConfirmAction] = useState(null) // 'delete' | 'manual_on' | 'manual_off' | 'unlock'
  const [chatFilter, setChatFilter] = useState('all') // 'all' | 'blocked'
  const [chatModeTab, setChatModeTab] = useState(isTester ? 'test' : 'real') // user/admin: real, tester: test
  const [simulateText, setSimulateText] = useState('')
  const [simulateLoading, setSimulateLoading] = useState(false)
  const [manualText, setManualText] = useState('')
  const [manualSending, setManualSending] = useState(false)
  const [attachedMedia, setAttachedMedia] = useState([]) // [{type, url, name}]
  const [uploadingMedia, setUploadingMedia] = useState(false) // loading indicator for file upload
  const [isRecording, setIsRecording] = useState(false)
  const recordingStreamRef = useRef(null)
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const [createTestLoading, setCreateTestLoading] = useState(false)
  const [addTestModalOpen, setAddTestModalOpen] = useState(false)
  /** outgoing model media: 'unloaded' | 'free' | 'full' — no binary fetch until user clicks ⬇ */
  const [outMediaByMsgId, setOutMediaByMsgId] = useState({})
  const [addTestModalModelId, setAddTestModalModelId] = useState(null)
  const [addTestModalConfigId, setAddTestModalConfigId] = useState(null)
  const [addTestModalFanName, setAddTestModalFanName] = useState('test_fan')
  const [editTestModalOpen, setEditTestModalOpen] = useState(false)
  const [editTestModalFanName, setEditTestModalFanName] = useState('')
  const [editTestModalConfigId, setEditTestModalConfigId] = useState(null)
  const [editTestLoading, setEditTestLoading] = useState(false)
  const [configsList, setConfigsList] = useState([])
  const [actionLoading, setActionLoading] = useState(false)
  const [logPanelOpen, setLogPanelOpen] = useState(false)
  const [logsList, setLogsList] = useState([])
  const [loadingLogsList, setLoadingLogsList] = useState(false)
  const [selectedGraphId, setSelectedGraphId] = useState(null)
  const [messageLog, setMessageLog] = useState(null)
  const [loadingLog, setLoadingLog] = useState(false)
  const [logError, setLogError] = useState(null)
  const [chatPage, setChatPage] = useState(0)
  const [chatsTotal, setChatsTotal] = useState(0)
  const [messagesTotal, setMessagesTotal] = useState(0)
  const [modelTyping, setModelTyping] = useState(false)
  const [fanBotProfile, setFanBotProfile] = useState(null)
  const [fanBotProfileLoading, setFanBotProfileLoading] = useState(false)
  const [fanBotProfileError, setFanBotProfileError] = useState(null)
  const chatLimit = 20

  useEffect(() => {
    let cancelled = false
    getMyCapabilities()
      .then((data) => {
        if (!cancelled) setCanUseDiagnostics(Boolean(data?.can_view_chat_diagnostics))
      })
      .catch(() => {
        if (!cancelled) setCanUseDiagnostics(isTesterOrAdminRole(role))
      })
    return () => {
      cancelled = true
    }
  }, [role])

  useEffect(() => {
    if (!canUseDiagnostics) setLogPanelOpen(false)
  }, [canUseDiagnostics])

  useEffect(() => {
    if (!selected || !canUseDiagnostics) {
      setFanBotProfile(null)
      setFanBotProfileError(null)
      return undefined
    }
    let cancelled = false
    setFanBotProfileLoading(true)
    setFanBotProfileError(null)
    getFanBotProfile(selected.modelId, selected.fanId)
      .then((d) => {
        if (!cancelled) setFanBotProfile(d)
      })
      .catch(() => {
        if (!cancelled) setFanBotProfileError('x')
      })
      .finally(() => {
        if (!cancelled) setFanBotProfileLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selected, canUseDiagnostics])

  useEffect(() => {
    if (addTestModalOpen || (fan?.is_test && selected) || editTestModalOpen) {
      listConfigs()
        .then((r) => setConfigsList(Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []))
        .catch(() => setConfigsList([]))
    }
  }, [addTestModalOpen, fan?.is_test, selected, editTestModalOpen])

  const stateRef = useRef({ modelId, chatModeTab, selected, logPanelOpen, selectedGraphId, chatPage, messagesLength: messages.length, canUseDiagnostics })
  stateRef.current = { modelId, chatModeTab, selected, logPanelOpen, selectedGraphId, chatPage, messagesLength: messages.length, canUseDiagnostics }
  const typingTimeoutRef = useRef(null)
  const messagesContainerRef = useRef(null)

  // Scroll to end of conversation when opening chat and on new messages (without animation)
  const scrollToBottom = () => {
    const el = messagesContainerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }

  useEffect(() => {
    if (!selected || loadingChat) return
    // RAF — after render, when layout is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => scrollToBottom())
    })
    // Re-scroll after images load (MediaThumbnail loads asynchronously)
    const t = setTimeout(scrollToBottom, 300)
    return () => clearTimeout(t)
  }, [selected, loadingChat, messages, modelTyping])

  useEffect(() => {
    const unsub = subscribeChatEvents((event) => {
      if (event?.type === 'model_typing') {
        const { selected: sel } = stateRef.current
        if (sel && Number(event.model_id) === Number(sel.modelId) && Number(event.fan_id) === Number(sel.fanId)) {
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
          setModelTyping(true)
          typingTimeoutRef.current = setTimeout(() => {
            setModelTyping(false)
            typingTimeoutRef.current = null
          }, 4500)
        }
        return
      }
      const { modelId: mid, chatModeTab: tab, selected: sel, logPanelOpen: logsOpen, selectedGraphId: graphId, chatPage: p, canUseDiagnostics: diagnosticsEnabled } = stateRef.current
      if (tab) {
        const offset = (p || 0) * 20
        getChatConversations(mid || undefined, tab === 'test', { limit: 20, offset })
          .then((r) => {
            setConversations(Array.isArray(r?.data) ? r.data : [])
            setChatsTotal(r?.total ?? 0)
          })
          .catch(() => {})
      }
      if (sel) {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
          typingTimeoutRef.current = null
        }
        setModelTyping(false)
        // Request enough messages to cover what's already loaded + new ones
        const currentLen = stateRef.current.messagesLength || 0
        const reqLimit = Math.max(100, currentLen + 50)
        getChatConversation(sel.modelId, sel.fanId, { limit: reqLimit, offset: 0 })
          .then((r) => {
            setMessages(Array.isArray(r?.data) ? r.data : [])
            setFan(r?.fan || null)
            setMessagesTotal(r?.total ?? 0)
          })
          .catch(() => {})
        if (diagnosticsEnabled) {
          getFanBotProfile(sel.modelId, sel.fanId)
            .then((profile) => setFanBotProfile(profile))
            .catch(() => {})
        }
        if (logsOpen) {
          getBotLogsList({ modelId: sel.modelId, fanId: sel.fanId })
            .then((r) => {
              const items = Array.isArray(r?.data) ? r.data : []
              setLogsList(items)
            })
            .catch(() => {})
          if (graphId) {
            getBotLogDetail(graphId)
              .then((r) => setMessageLog(r))
              .catch(() => {})
          }
        }
      }
    })
    return unsub
  }, [])

  useEffect(() => {
    getModels()
      .then((r) => {
        const list = Array.isArray(r) ? r : (Array.isArray(r?.data) ? r.data : [])
        setModels(list)
      })
      .catch(() => setModels([]))
      .finally(() => setLoadingModels(false))
  }, [])

  useEffect(() => {
    setChatPage(0)
  }, [modelId, chatModeTab])

  // Polling fallback: refresh conversation list every 8s
  useEffect(() => {
    const interval = setInterval(() => {
      const { modelId: mid, chatModeTab: tab, chatPage: p } = stateRef.current
      const isTest = tab === 'test'
      const offset = (p || 0) * chatLimit
      getChatConversations(mid || undefined, isTest, { limit: chatLimit, offset })
        .then((r) => {
          setConversations(Array.isArray(r?.data) ? r.data : [])
          setChatsTotal(r?.total ?? 0)
        })
        .catch(() => {})
    }, 8000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    setLoading(true)
    setChatsError(null)
    setInputError(null)
    const isTest = chatModeTab === 'test'
    const offset = chatPage * chatLimit
    getChatConversations(modelId || undefined, isTest, { limit: chatLimit, offset })
      .then((r) => {
        setConversations(Array.isArray(r?.data) ? r.data : [])
        setChatsTotal(r?.total ?? 0)
        setSelected(null)
        setMessages([])
        setFan(null)
        setSelectedGraphId(null)
        setMessageLog(null)
        setLogError(null)
      })
      .catch((err) => {
        setConversations([])
        setChatsTotal(0)
        setChatsError(err?.message || t('chats.error.load'))
      })
      .finally(() => setLoading(false))
  }, [modelId, chatModeTab, chatPage])

  useEffect(() => {
    if (!selected) {
      setMessages([])
      setFan(null)
      setLogsList([])
      setSelectedGraphId(null)
      setMessageLog(null)
      setLogError(null)
      return
    }
    setLoadingChat(true)
    getChatConversation(selected.modelId, selected.fanId)
      .then((r) => {
        setMessages(Array.isArray(r?.data) ? r.data : [])
        setFan(r?.fan || null)
        setMessagesTotal(r?.total ?? 0)
        setSelectedGraphId(null)
        setMessageLog(null)
        setLogError(null)
      })
      .catch(() => {
        setMessages([])
        setFan(null)
        setMessagesTotal(0)
      })
      .finally(() => setLoadingChat(false))
  }, [selected])

  useEffect(() => {
    setOutMediaByMsgId({})
    setManualText('')
  }, [selected])

  // Polling fallback: refresh messages every 5s when a chat is selected
  // Ensures LLM responses appear without page reload even if SSE fails
  useEffect(() => {
    if (!selected) return
    const interval = setInterval(() => {
      const { selected: sel } = stateRef.current
      if (!sel) return
      getChatConversation(sel.modelId, sel.fanId)
        .then((r) => {
          const newMsgs = Array.isArray(r?.data) ? r.data : []
          setMessages((prev) => {
            if (newMsgs.length !== prev.length) return newMsgs
            if (newMsgs.length > 0 && prev.length > 0 && newMsgs[newMsgs.length - 1]?.id !== prev[prev.length - 1]?.id) return newMsgs
            return prev
          })
          setFan(r?.fan || null)
        })
        .catch(() => {})
    }, 5000)
    return () => clearInterval(interval)
  }, [selected])

  useEffect(() => {
    if (!selected || !logPanelOpen) {
      setLogsList([])
      setLoadingLogsList(false)
      return
    }
    setLoadingLogsList(true)
        getBotLogsList({ modelId: selected.modelId, fanId: selected.fanId })
          .then((r) => {
            const items = Array.isArray(r?.data) ? r.data : []
            setLogsList(items)
          })
      .catch(() => setLogsList([]))
      .finally(() => setLoadingLogsList(false))
  }, [selected, logPanelOpen])

  useEffect(() => {
    if (!selectedGraphId) {
      setMessageLog(null)
      return
    }
    setLoadingLog(true)
    setLogError(null)
    getBotLogDetail(selectedGraphId)
      .then((r) => setMessageLog(r))
      .catch((err) => {
        setMessageLog(null)
        setLogError(err?.message || t('chats.logs.error'))
      })
      .finally(() => setLoadingLog(false))
  }, [selectedGraphId])

  const isSelected = (c) => selected && selected.modelId === c.model_id && selected.fanId === c.fan_id
  const filteredConversations = conversations.filter((c) => (chatFilter === 'blocked' ? Boolean(c.is_safety_lock) : true))
  const selectedConversation = selected
    ? conversations.find((c) => c.model_id === selected.modelId && c.fan_id === selected.fanId) || null
    : null
  const interactionStyle = fanBotProfile?.interaction_style && typeof fanBotProfile.interaction_style === 'object'
    ? fanBotProfile.interaction_style
    : (fanBotProfile?.parameters?.interaction_style_runtime || {})
  const interactionTraits = interactionStyle?.traits && typeof interactionStyle.traits === 'object'
    ? interactionStyle.traits
    : {}
  const buyerStyle = fanBotProfile?.buyer_style_hint
    || fanBotProfile?.user_profile?.buyer_style_hint
    || 'unknown'
  const conversationStyle = fanBotProfile?.conversation_style_hint
    || fanBotProfile?.user_profile?.conversation_style_hint
    || 'unknown'

  const refreshConversations = async () => {
    const isTest = chatModeTab === 'test'
    const offset = chatPage * chatLimit
    const r = await getChatConversations(modelId || undefined, isTest, { limit: chatLimit, offset })
    setConversations(Array.isArray(r?.data) ? r.data : [])
    setChatsTotal(r?.total ?? 0)
  }

  const handleLoadEarlierMessages = async () => {
    if (!selected) return
    const earlierLimit = 100
    const earlierOffset = messages.length
    try {
      const r = await getChatConversation(selected.modelId, selected.fanId, { limit: earlierLimit, offset: earlierOffset })
      const earlier = Array.isArray(r?.data) ? r.data : []
      if (earlier.length > 0) {
        // Save scroll position
        const el = messagesContainerRef.current
        const prevScrollHeight = el?.scrollHeight || 0
        // Prepend earlier messages (they are older, come first)
        setMessages((prev) => [...earlier, ...prev])
        setMessagesTotal(r?.total ?? 0)
        // Restore scroll position after render
        requestAnimationFrame(() => {
          if (el) {
            const newScrollHeight = el.scrollHeight
            el.scrollTop = newScrollHeight - prevScrollHeight
          }
        })
      }
    } catch (err) {
      console.error('Failed to load earlier messages:', err)
    }
  }

  useEffect(() => {
    if (editTestModalOpen && fan) {
      setEditTestModalFanName(fan.username || fan.name || '')
      setEditTestModalConfigId(fan.config_id != null ? String(fan.config_id) : '')
    }
  }, [editTestModalOpen, fan?.id, fan?.username, fan?.name, fan?.config_id])

  const handleEditTestChat = async () => {
    if (!selected) return
    const fanName = (editTestModalFanName || '').trim() || 'test_fan'
    setEditTestLoading(true)
    try {
      const r = await updateTestChat(selected.modelId, selected.fanId, {
        username: fanName,
        name: fanName,
        configId: editTestModalConfigId && editTestModalConfigId !== '' ? Number(editTestModalConfigId) : null,
      })
      setFan((prev) => (prev ? { ...prev, username: r?.username ?? fanName, name: r?.name ?? fanName, config_id: r?.config_id ?? null, config_name: r?.config_name ?? null } : null))
      await refreshConversations()
      setEditTestModalOpen(false)
    } catch (err) {
      setChatsError(err?.message || t('chats.error.load'))
    } finally {
      setEditTestLoading(false)
    }
  }

  const handleCreateTestChat = async () => {
    const mid = addTestModalModelId
    if (!mid) return
    const fanName = (addTestModalFanName || '').trim() || 'test_fan'
    setCreateTestLoading(true)
    try {
      await createTestChat(mid, {
        username: fanName,
        name: fanName,
        configId: addTestModalConfigId || (isTester ? undefined : null),
      })
      setAddTestModalOpen(false)
      setAddTestModalModelId(null)
      setAddTestModalConfigId(null)
      setAddTestModalFanName('test_fan')
      await refreshConversations()
    } catch (err) {
      setChatsError(err?.message || t('chats.error.load'))
    } finally {
      setCreateTestLoading(false)
    }
  }

  const handleSimulateMessage = async () => {
    if (!selected || (!simulateText.trim() && attachedMedia.length === 0)) return
    const { modelId: mid, fanId: fid } = selected
    setSimulateLoading(true)
    try {
      const media = attachedMedia.length > 0
        ? attachedMedia.map(m => ({ type: m.type, url: m.url }))
        : null
      await simulateIncomingMessage(mid, fid, simulateText.trim(), media)
      setSimulateText('')
      setAttachedMedia([])
      const r = await getChatConversation(mid, fid)
      setMessages(Array.isArray(r?.data) ? r.data : [])
      setTimeout(async () => {
        const r2 = await getChatConversation(mid, fid)
        setMessages(Array.isArray(r2?.data) ? r2.data : [])
      }, 5000)
    } catch (err) {
      setChatsError(err?.message || t('chats.error.load'))
    } finally {
      setSimulateLoading(false)
    }
  }

  const handleManualMessage = async () => {
    if (!selected || !manualText.trim()) return
    const { modelId: mid, fanId: fid } = selected
    setManualSending(true)
    setChatsError(null)
    try {
      await sendManualConversationMessage(mid, fid, manualText.trim())
      setManualText('')
      const r = await getChatConversation(mid, fid)
      setMessages(Array.isArray(r?.data) ? r.data : [])
      setFan(r?.fan || null)
      setMessagesTotal(r?.total ?? 0)
      await refreshConversations()
    } catch (err) {
      setChatsError(err?.message || t('chats.manualSend.error') || 'Failed to send manual reply')
    } finally {
      setManualSending(false)
    }
  }

  const MAX_UPLOAD_SIZE = 10 * 1024 * 1024 // 10 MB - matches backend limit

  const handleAttachFile = (mediaType) => async (e) => {
    const file = e.target.files?.[0]
    if (!file || !selected) return
    setInputError(null)

    // Client-side file size validation
    if (file.size > MAX_UPLOAD_SIZE) {
      setInputError(t('chats.error.fileTooLarge') || `File too large. Max: ${MAX_UPLOAD_SIZE / (1024 * 1024)} MB`)
      e.target.value = ''
      return
    }

    setUploadingMedia(true)
    try {
      const result = await uploadTestMedia(selected.modelId, selected.fanId, file)
      setAttachedMedia(prev => [...prev, { type: mediaType, url: result.url, name: file.name }])
    } catch (err) {
      setInputError(err?.message || t('chats.error.load'))
    } finally {
      setUploadingMedia(false)
    }
    e.target.value = ''
  }

  const handleAttachPhoto = handleAttachFile('photo')

  // Audio recording in browser
  const startRecording = async () => {
    try {
      setInputError(null)
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        setInputError(t('chats.error.microphone') || 'Microphone access denied')
        return
      }
      if (typeof MediaRecorder === 'undefined') {
        setInputError(t('chats.error.microphone') || 'Microphone access denied')
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      recordingStreamRef.current = stream

      const preferredTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/aac',
      ]
      const supported = preferredTypes.find((mt) => MediaRecorder.isTypeSupported?.(mt))
      const options = supported ? { mimeType: supported } : undefined
      const mediaRecorder = options ? new MediaRecorder(stream, options) : new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = async () => {
        try {
          const mimeType = mediaRecorder.mimeType || (supported || 'audio/webm')
          const blob = new Blob(audioChunksRef.current, { type: mimeType })
          if (!blob.size) {
            setInputError(t('chats.error.microphone') || 'Microphone access denied')
            return
          }

          const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('aac') ? 'aac' : 'webm'
          const file = new File([blob], `recording_${Date.now()}.${ext}`, { type: mimeType })

          if (file.size > MAX_UPLOAD_SIZE) {
            setInputError(t('chats.error.fileTooLarge') || `File too large. Max: ${MAX_UPLOAD_SIZE / (1024 * 1024)} MB`)
            return
          }

          setUploadingMedia(true)
          try {
            const result = await uploadTestMedia(selected.modelId, selected.fanId, file)
            setAttachedMedia((prev) => [...prev, { type: 'audio', url: result.url, name: file.name }])
          } catch (err) {
            setInputError(err?.message || t('chats.error.load'))
          } finally {
            setUploadingMedia(false)
          }
        } finally {
          try {
            recordingStreamRef.current?.getTracks?.().forEach((tr) => tr.stop())
          } catch (_) {}
          recordingStreamRef.current = null
        }
      }

      mediaRecorder.start()
      setIsRecording(true)
    } catch (err) {
      setInputError(t('chats.error.microphone') || 'Microphone access denied')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop()
      } catch (_) {
        try {
          recordingStreamRef.current?.getTracks?.().forEach((tr) => tr.stop())
        } catch (_) {}
        recordingStreamRef.current = null
      }
      setIsRecording(false)
    }
  }

  const toggleRecording = () => {
    if (isRecording) stopRecording()
    else startRecording()
  }

  const removeAttachment = (index) => {
    setAttachedMedia(prev => prev.filter((_, i) => i !== index))
  }

  const handleLoadOutMedia = (msg) => {
    if (!msg?.id) return
    setOutMediaByMsgId((p) => ({
      ...p,
      [msg.id]: outMediaModeAfterUserLoadClick(msg),
    }))
  }

  const handleBuy = async (msg) => {
    if (!selected) return
    setSimulateLoading(true)
    try {
      const price = msg.purchase_offer?.price ?? 0
      await simulatePurchase(selected.modelId, selected.fanId, {
        messageId: msg.id,
        amount: price,
        purchaseType: 'ppv_unlock',
      })
      const r = await getChatConversation(selected.modelId, selected.fanId)
      setMessages(Array.isArray(r?.data) ? r.data : [])
      if (msg?.id) {
        setOutMediaByMsgId((p) => ({ ...p, [msg.id]: 'full' }))
      }
    } catch (err) {
      setChatsError(err?.message || t('chats.error.load'))
    } finally {
      setSimulateLoading(false)
    }
  }

  const handleCopyChatHistory = async () => {
    if (!selected) return
    try {
      const { exportChatHistory } = await import('../api/model_data.js')
      const result = await exportChatHistory(selected.modelId, selected.fanId)
      if (result?.text) {
        await navigator.clipboard.writeText(result.text)
        alert(t('common.copied') || 'Chat history copied to clipboard')
      }
    } catch (err) {
      console.error('Failed to copy chat history:', err)
      alert(t('common.error') || 'Error copying chat history')
    }
  }

  const [copyingLogsList, setCopyingLogsList] = useState(false)
  const handleCopyConversationLogsList = async () => {
    if (copyingLogsList) return
    setCopyingLogsList(true)
    try {
      const payload = {
        model_id: selected?.modelId ?? null,
        fan_id: selected?.fanId ?? null,
        logs: logsList,
      }
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2))
      alert(t('common.copied') || 'Copied to clipboard')
    } catch (err) {
      console.error('Failed to copy conversation logs list:', err)
      alert(t('common.error') || 'Error')
    } finally {
      setCopyingLogsList(false)
    }
  }

  const handleDeleteChat = async () => {
    if (!selected) return
    setActionLoading(true)
    try {
      await purgeChatConversation(selected.modelId, selected.fanId)
      await refreshConversations()
      setSelected(null)
      setMessages([])
      setFan(null)
    } finally {
      setActionLoading(false)
      setConfirmAction(null)
    }
  }

  const handleSetManualMode = async (enabled) => {
    if (!selected) return
    const pair = { ...selected }
    const stillSelected = () => String(stateRef.current.selected?.modelId) === String(pair.modelId)
      && String(stateRef.current.selected?.fanId) === String(pair.fanId)
    setActionLoading(true)
    setChatsError(null)
    try {
      const result = enabled ? await setChatManualMode(pair.modelId, pair.fanId, true)
        : await resumeWithSafetyReview(
          (review) => setChatManualMode(pair.modelId, pair.fanId, false, review),
          (message) => stillSelected() && window.confirm(message),
          `model #${pair.modelId}, fan #${pair.fanId}`,
          t,
        )
      if (!result) return
      if (!stillSelected()) { await refreshConversations(); return }
      setFan((prev) => (
        prev ? { ...prev, is_manual_mode: result?.is_manual_mode ?? enabled } : prev
      ))
      await refreshConversations()
    } catch (err) {
      setChatsError(err?.message || t('chats.manual.error') || 'Failed to change manual mode')
    } finally {
      setActionLoading(false)
      setConfirmAction(null)
    }
  }

  const handleUnlockSafetyLock = async () => {
    if (!selected) return
    const pair = { ...selected }
    const stillSelected = () => String(stateRef.current.selected?.modelId) === String(pair.modelId)
      && String(stateRef.current.selected?.fanId) === String(pair.fanId)
    setActionLoading(true)
    try {
      const { safety_lock: lock } = await getChatSafetyStatus(pair.modelId, pair.fanId)
      if (!stillSelected()) return
      if (!lock?.locked) { await refreshConversations(); return }
      if (lock?.locked && !window.confirm(safetyReviewMessage(lock, { target: `model #${pair.modelId}, fan #${pair.fanId}`, t }))) return
      const result = await unlockChatSafetyLock(pair.modelId, pair.fanId, lock?.confirmation_token)
      if (!stillSelected()) { await refreshConversations(); return }
      setFan((prev) => (
        prev && typeof result?.is_manual_mode === 'boolean'
          ? { ...prev, is_manual_mode: result.is_manual_mode }
          : prev
      ))
      await refreshConversations()
    } catch (err) {
      setChatsError(err?.message || 'Failed to unlock safety lock')
    } finally {
      setActionLoading(false)
      setConfirmAction(null)
    }
  }

  const handleSetPpvPermission = async (enabled) => {
    if (!selected || !fan || actionLoading) return
    const pair = { ...selected }
    const stillSelected = () => String(stateRef.current.selected?.modelId) === String(pair.modelId)
      && String(stateRef.current.selected?.fanId) === String(pair.fanId)
    setActionLoading(true)
    setChatsError(null)
    try {
      const result = await setChatPpvPermission(pair.modelId, pair.fanId, enabled,
        fan.ppv_permission_revision ?? selectedConversation?.ppv_permission_revision)
      if (stillSelected()) {
        setFan((prev) => prev ? { ...prev, ppv_sales_enabled: result.ppv_sales_enabled,
          ppv_permission_revision: result.ppv_permission_revision } : prev)
      }
      await refreshConversations()
    } catch (err) {
      if (stillSelected()) setChatsError(err?.message || 'Failed to change PPV permission; refresh the conversation')
    } finally {
      setActionLoading(false)
    }
  }

  const handleConfirmAction = async () => {
    if (confirmAction === 'delete') return handleDeleteChat()
    if (confirmAction === 'manual_on') return handleSetManualMode(true)
    if (confirmAction === 'manual_off') return handleSetManualMode(false)
    if (confirmAction === 'unlock') return handleUnlockSafetyLock()
  }

  const handleSelectMessage = (msg) => {
    if (msg.direction !== 'out') return
    const matchingLog = logsList.find((g) => g.outgoing_message_id === msg.id)
    if (matchingLog) {
      setLogPanelOpen(true)
      setSelectedGraphId(matchingLog.id)
    } else {
      getBotMessageLog(msg.id)
        .then((log) => {
          setLogPanelOpen(true)
          setSelectedGraphId(log?.graph_instance?.id || null)
        })
        .catch(() => setLogError(t('chats.logs.error')))
    }
  }

  const handleSelectLog = (graphId) => {
    setSelectedGraphId((prev) => (prev === graphId ? null : graphId))
  }

  const showLogPanel = logPanelOpen && selected

  const options = models.length
    ? [{ value: '', label: t('chats.filter.allModels') }, ...models.map((m) => ({ value: String(m.id), label: m.of_email || m.name || t('common.notAvailable') }))]
    : [{ value: '', label: t('chats.loading') }]

  return (
    <>
    <PageSection
      className="chats-page"
      title={t('chats.title')}
    >
      <>
        <FiltersRow>
          {canSwitchTestReal && (
            <FilterGroup label={t('chats.filter.type')}>
              <div className="filter-tabs">
                <button
                  type="button"
                  className={`filter-tab-btn ${chatModeTab === 'real' ? 'filter-tab-btn--active' : ''}`}
                  onClick={() => setChatModeTab('real')}
                >
                  {t('chats.tab.real')}
                </button>
                <button
                  type="button"
                  className={`filter-tab-btn ${chatModeTab === 'test' ? 'filter-tab-btn--active' : ''}`}
                  onClick={() => setChatModeTab('test')}
                >
                  {t('chats.tab.test')}
                </button>
              </div>
            </FilterGroup>
          )}
          <FilterGroup label={t('chats.filter.model')}>
            <Select
              className="filter-select"
              value={modelId != null ? String(modelId) : ''}
              placeholder={t('chats.modelFilter.placeholder')}
              options={options}
              onChange={(v) => setModelId(v ? Number(v) : null)}
              dropdownPosition="down"
            />
            {modelId && (
              <Link to={`/old/models/${modelId}`} className="filter-link">
                {t('chats.toModel')}
              </Link>
            )}
          </FilterGroup>
          <FilterGroup label={t('chats.filter.status')}>
            <div className="filter-tabs">
              <button
                type="button"
                className={`filter-tab-btn ${chatFilter === 'all' ? 'filter-tab-btn--active' : ''}`}
                onClick={() => setChatFilter('all')}
              >
                {t('chats.filter.showAll')}
              </button>
              <button
                type="button"
                className={`filter-tab-btn ${chatFilter === 'blocked' ? 'filter-tab-btn--active' : ''}`}
                onClick={() => setChatFilter('blocked')}
              >
                {t('chats.filter.showBlocked')}
              </button>
            </div>
          </FilterGroup>
        </FiltersRow>
        {loadingModels ? (
        <p className="chats-loading">{t('chats.loadingModels')}</p>
      ) : loading ? (
        <p className="chats-loading">{t('chats.loading')}</p>
      ) : (
        <div className="chats-layout">
          <aside className="chats-sidebar">
            {chatModeTab === 'test' && (
              <div className="chats-sidebar-add">
                <button
                  type="button"
                  className="chats-add-test-btn"
                  onClick={() => {
                    setAddTestModalModelId(null)
                    setAddTestModalOpen(true)
                  }}
                >
                  {t('chats.addTestChat')}
                </button>
              </div>
            )}
            <div className="chats-conversations-section">
              {inputError && !chatsError && (
                <p className="chats-sidebar-error">{inputError}</p>
              )}
              <div className="chats-conversations-scroll">
                {loading && conversations.length === 0 ? (
                  <p className="chats-sidebar-loading">{t('chats.sidebar.loading')}</p>
                ) : chatsError ? (
                  <p className="chats-sidebar-error">{chatsError}</p>
                ) : filteredConversations.length === 0 ? (
                  <p className="chats-sidebar-empty">{t('chats.sidebar.empty')}</p>
                ) : (
                  <ul className="chats-list">
                    {filteredConversations.map((c) => (
                      <li key={`${c.model_id}-${c.fan_id}`}>
                        <button
                          type="button"
                          className={`chats-list-item ${isSelected(c) ? 'chats-list-item--active' : ''}`}
                          onClick={() => {
                            setSelected({ modelId: c.model_id, fanId: c.fan_id })
                            setAttachedMedia([]) // Reset attachments when switching chat
                            setInputError(null)
                          }}
                        >
                          <div className="chats-list-item-avatar">
                            {(c.username || c.name || '?')[0].toUpperCase()}
                          </div>
                          <div className="chats-list-item-body">
                            <div className="chats-list-item-name">
                              {c.username || c.name || (c.external_id ? `ID ${c.external_id}` : '?')}
                              {c.is_test && <span className="chats-list-item-badge">{t('chats.testBadge')}</span>}
                            </div>
                            <div className="chats-list-item-model">{c.model_email || t('chats.modelFallback', { id: c.model_id })}</div>
                            <div className="chats-list-item-config">{c.config_name || t('chats.addTestChat.configActive')}</div>
                            {c.is_safety_lock && (
                              <div className="chats-list-item-preview">{t('chats.safetyLock.badge')}</div>
                            )}
                            <div className="chats-list-item-preview">{c.preview || t('chats.noMessages')}</div>
                            {c.last_at && (
                              <div className="chats-list-item-time">
                                {new Date(c.last_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </div>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {chatsTotal > 0 && (
                <Pagination
                  page={chatPage}
                  total={chatsTotal}
                  limit={chatLimit}
                  onPageChange={setChatPage}
                  loading={loading}
                  t={t}
                />
              )}
            </div>
          </aside>
          <main className={`chats-main ${showLogPanel ? 'chats-main--with-log' : ''}`}>
            {!selected ? (
              <div className="chats-main-placeholder">
                {t('chats.selectLeft')}
              </div>
            ) : loadingChat ? (
              <p className="chats-main-loading">{t('chats.thread.loading')}</p>
            ) : (
              <>
                <div className="chats-main-header">
                  <div className="chats-main-header-avatar">
                    {(fan?.username || fan?.name || '?')[0]?.toUpperCase()}
                  </div>
                  <div className="chats-main-header-info">
                    <span className="chats-main-header-name">
                      {fan?.username || fan?.name || (fan?.external_id ? `ID ${fan?.external_id}` : '?')}
                      {fan?.is_test && <span className="chats-list-item-badge">{t('chats.testBadge')}</span>}
                    </span>
                    <span className="chats-main-header-id">@{fan?.username || fan?.external_id || '?'}</span>
                    {fan?.subscription?.active && (
                      <span className="chats-main-header-subscription">
                        <strong>{t('chats.subscription.active')}</strong>
                        {fan.subscription.tier_name ? ` · ${fan.subscription.tier_name}` : ''}
                        {Number(fan.subscription.price) > 0
                          ? ` · ${Number(fan.subscription.price).toFixed(2)} ${fan.subscription.currency || 'USD'}`
                          : ''}
                      </span>
                    )}
                    {canManage && selected && (
                      <Link to={`/old/models/${selected.modelId}`} className="chats-main-header-link">
                        {t('chats.toModel')}
                      </Link>
                    )}
                  </div>
                  <div className="chats-main-header-actions">
                    {canManage && <label className="chats-action-btn" title="PPV permission is independent of VIP. Core and safety gates still apply; enabling does not send anything now.">
                      <input type="checkbox" checked={Boolean(fan?.ppv_sales_enabled ?? selectedConversation?.ppv_sales_enabled ?? !(fan?.is_vip_mode ?? selectedConversation?.is_vip_mode))}
                        disabled={actionLoading || !fan} onChange={(event) => handleSetPpvPermission(event.target.checked)} /> Allow PPV
                    </label>}
                    {fan?.is_test && (isTester || canSwitchTestReal) && (
                        <button
                          type="button"
                          className="chats-action-btn chats-action-btn--icon"
                          onClick={() => setEditTestModalOpen(true)}
                          title={t('chats.editTestChat.title')}
                          aria-label={t('chats.editTestChat.title')}
                        >
                          <IconEdit />
                        </button>
                    )}
                    {canManage && !fan?.is_test && (
                      <button
                        type="button"
                        className={`chats-action-btn ${fan?.is_manual_mode ? 'chats-action-btn--active' : ''}`}
                        disabled={actionLoading}
                        onClick={() => fan?.is_manual_mode && selectedConversation?.is_safety_lock
                          ? handleSetManualMode(false)
                          : setConfirmAction(fan?.is_manual_mode ? 'manual_off' : 'manual_on')}
                      >
                        {fan?.is_manual_mode ? t('chats.manual.disable') : t('chats.manual.enable')}
                      </button>
                    )}
                    {canManage && selectedConversation?.is_safety_lock && (
                      <button
                        type="button"
                        className="chats-action-btn chats-action-btn--danger"
                        disabled={actionLoading}
                        onClick={handleUnlockSafetyLock}
                      >
                        {t('chats.safetyLock.unlock')}
                      </button>
                    )}
                    {canUseDiagnostics && <button
                      type="button"
                      className={`chats-action-btn chats-action-btn--icon ${logPanelOpen ? 'chats-action-btn--active' : ''}`}
                      onClick={() => setLogPanelOpen((p) => !p)}
                      title={t('chats.logs.title')}
                      aria-label={t('chats.logs.title')}
                    >
                      <IconLogs />
                    </button>}
                    {canUseDiagnostics && <button
                      type="button"
                      className="chats-action-btn chats-action-btn--icon"
                      onClick={() => handleCopyChatHistory()}
                      title={t('chats.export.chatHistory') || 'Copy chat history'}
                      aria-label={t('chats.export.chatHistory') || 'Copy chat history'}
                    >
                      📋
                    </button>}
                    {canAdminister && <button
                      type="button"
                      className="chats-action-btn chats-action-btn--icon chats-action-btn--danger"
                      onClick={() => setConfirmAction('delete')}
                      title={t('chats.delete.action')}
                      aria-label={t('chats.delete.action')}
                    >
                      <IconTrash />
                    </button>}
                  </div>
                </div>
                <SafetyLockNotice lock={selectedConversation?.safety_lock} locked={selectedConversation?.is_safety_lock} />
                {canUseDiagnostics && <details className="chats-funnel-diagnostics" style={{ padding: '0 1rem 0.5rem' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{t('chats.funnel.title')}</summary>
                  {fanBotProfileLoading && <p className="chats-sidebar-loading">{t('chats.funnel.loading')}</p>}
                  {fanBotProfileError && <p className="chats-sidebar-error">{t('chats.funnel.error')}</p>}
                  {fanBotProfile && !fanBotProfileLoading && (
                    <dl className="chats-funnel-grid">
                      <dt>conversation_state</dt>
                      <dd>{fanBotProfile.conversation_state ?? '—'}</dd>
                      <dt>current_state</dt>
                      <dd>{fanBotProfile.current_state ?? '—'}</dd>
                      <dt>postbuy_phase</dt>
                      <dd>{fanBotProfile.postbuy_phase ?? '—'}</dd>
                      <dt>sale_script_allowed</dt>
                      <dd>{String(fanBotProfile.sale_script_allowed ?? '—')}</dd>
                      <dt>direct_offer_allowed</dt>
                      <dd>{String(fanBotProfile.direct_offer_allowed ?? '—')}</dd>
                      <dt>upsell_allowed</dt>
                      <dd>{String(fanBotProfile.upsell_allowed ?? '—')}</dd>
                      <dt>cooldown_after_offer</dt>
                      <dd>{String(fanBotProfile.cooldown_after_offer ?? '—')}</dd>
                      <dt>relationship_stage</dt>
                      <dd>{fanBotProfile.relationship_stage ?? '—'}</dd>
                      <dt>monetization_stage</dt>
                      <dd>{fanBotProfile.monetization_stage ?? '—'}</dd>
                      <dt>{t('chats.funnel.buyerStyle')}</dt>
                      <dd>{buyerStyle}</dd>
                      <dt>{t('chats.funnel.conversationStyle')}</dt>
                      <dd>{conversationStyle}</dd>
                      <dt>{t('chats.funnel.adaptiveStyle')}</dt>
                      <dd>
                        {interactionStyle?.status
                          || (interactionStyle?.active ? t('chats.funnel.style.active') : t('chats.funnel.style.noData'))}
                      </dd>
                      <dt>{t('chats.funnel.style.confidence')}</dt>
                      <dd>{Number.isFinite(Number(interactionStyle?.confidence)) ? `${Number(interactionStyle.confidence)}%` : '—'}</dd>
                      {INTERACTION_STYLE_FIELDS.map((row) => (
                        <React.Fragment key={row.key}>
                          <dt>{t(row.labelKey)}</dt>
                          <dd>{interactionTraits[row.key] ?? '—'}</dd>
                        </React.Fragment>
                      ))}
                      {L3_SCHEMA.map((row) => (
                        <React.Fragment key={row.key}>
                          <dt>{row.key}</dt>
                          <dd>{fanBotProfile.parameters?.[row.key] ?? '—'}</dd>
                        </React.Fragment>
                      ))}
                      <dt>current_script_session_id</dt>
                      <dd>{fanBotProfile.current_script_session_id ?? '—'}</dd>
                      <dt>current_script_stage_index</dt>
                      <dd>{fanBotProfile.current_script_stage_index ?? '—'}</dd>
                    </dl>
                  )}
                </details>}
                <div className="chats-main-body">
                  <div ref={messagesContainerRef} className="chats-messages">
                    {messagesTotal > messages.length && (
                      <button
                        type="button"
                        className="chats-load-earlier-btn"
                        onClick={handleLoadEarlierMessages}
                      >
                        {t('chats.loadEarlier') || `↑ Load earlier messages (${messagesTotal - messages.length} more)`}
                      </button>
                    )}
                    {messages.map((msg) => {
                      const msgLog = logsList.find((g) => g.outgoing_message_id === msg.id)
                      const isLogSelected = selectedGraphId && msgLog?.id === selectedGraphId
                      return (
                        <MessageBubble
                          key={msg.id}
                          msg={msg}
                          modelId={selected?.modelId}
                          t={t}
                          selected={isLogSelected}
                          onSelect={handleSelectMessage}
                          onBuy={handleBuy}
                          isTestChat={Boolean(fan?.is_test)}
                          outMediaMode={msg.id != null ? (outMediaByMsgId[msg.id] ?? 'unloaded') : 'unloaded'}
                          onLoadOutMedia={handleLoadOutMedia}
                        />
                      )
                    })}
                    {messages.length === 0 && !modelTyping && <p className="chats-messages-empty">{t('chats.noMessages')}</p>}
                    {modelTyping && (
                      <div className="chats-typing-indicator chats-typing-indicator--model" role="status" aria-label={t('chats.typing')}>
                        <span className="chats-typing-dots">
                          <span /><span /><span />
                        </span>
                      </div>
                    )}
                  </div>
                  {fan?.is_test && (
                    <div className="chats-input-bar">
                      {/* Photo attachment button */}
                      <label className={`chats-input-attach-btn ${uploadingMedia ? 'chats-input-attach-btn--loading' : ''}`} title={uploadingMedia ? t('chats.simulate.uploading') : t('chats.simulate.attachPhoto')}>
                        {uploadingMedia ? '⏳' : '📷'}
                        <input
                          type="file"
                          accept="image/*,.heic,.heif"
                          hidden
                          onChange={handleAttachPhoto}
                          disabled={uploadingMedia}
                        />
                      </label>
                      {/* Audio recording button */}
                      <button
                        type="button"
                        className={`chats-input-attach-btn ${isRecording ? 'chats-input-attach-btn--recording' : ''} ${uploadingMedia ? 'chats-input-attach-btn--loading' : ''}`}
                        title={isRecording ? t('chats.simulate.stopRecording') : t('chats.simulate.recordAudio')}
                        onClick={toggleRecording}
                        disabled={uploadingMedia}
                      >
                        {uploadingMedia ? '⏳' : isRecording ? '⏹️' : '🎤'}
                      </button>
                      {/* Attached media preview */}
                      {attachedMedia.length > 0 && (
                        <div className="chats-input-attachments-preview">
                          {attachedMedia.map((f, i) => (
                            <span key={i} className="chats-input-attachment-chip">
                              {f.type === 'photo' ? '📷' : '🎤'} {f.name}
                              {f.type === 'audio' && (f.url || '').startsWith('/api/') && (
                                <AuthedAudio url={f.url} className="chats-input-attachment-audio" />
                              )}
                              {f.type === 'audio' && !(f.url || '').startsWith('/api/') && f.url && (
                                <audio className="chats-input-attachment-audio" controls preload="none" src={f.url} />
                              )}
                              <button type="button" onClick={() => removeAttachment(i)}>×</button>
                            </span>
                          ))}
                        </div>
                      )}
                      <input
                        type="text"
                        className="chats-input-bar-field"
                        placeholder={t('chats.simulate.messagePlaceholder')}
                        value={simulateText}
                        onChange={(e) => setSimulateText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSimulateMessage()}
                      />
                      <button
                        type="button"
                        className="chats-input-bar-btn"
                        onClick={handleSimulateMessage}
                        disabled={simulateLoading || (!simulateText.trim() && attachedMedia.length === 0)}
                      >
                        {simulateLoading ? t('common.saving') : t('chats.simulate.send')}
                      </button>
                    </div>
                  )}
                  {canManage && !fan?.is_test && fan?.is_manual_mode && (
                    <div className="chats-input-bar chats-input-bar--manual">
                      <input
                        type="text"
                        className="chats-input-bar-field"
                        placeholder={t('chats.manualSend.placeholder') || 'Write as the model...'}
                        value={manualText}
                        onChange={(e) => setManualText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleManualMessage()}
                      />
                      <button
                        type="button"
                        className="chats-input-bar-btn"
                        onClick={handleManualMessage}
                        disabled={manualSending || !manualText.trim()}
                      >
                        {manualSending ? t('common.saving') : (t('chats.manualSend.send') || 'Send reply')}
                      </button>
                    </div>
                  )}
                  {canManage && !fan?.is_test && !fan?.is_manual_mode && (
                    <div className="chats-input-note">
                      {t('chats.manualSend.enableHint') || 'Enable manual mode to answer from this cabinet.'}
                    </div>
                  )}
                </div>
              </>
            )}
          </main>
          {canUseDiagnostics && showLogPanel && (
            <aside className="chats-log-panel">
              <div className="chats-log-header">
                <h3 className="chats-log-title">{t('chats.logs.title')}</h3>
                <button
                  type="button"
                  className="chats-action-btn chats-action-btn--icon"
                  onClick={handleCopyConversationLogsList}
                  disabled={copyingLogsList}
                  title={t('chats.logs.copyLog') || 'Copy log'}
                  aria-label={t('chats.logs.copyLog') || 'Copy log'}
                >
                  {copyingLogsList ? '...' : '📋'}
                </button>
                <button type="button" className="chats-log-close" onClick={() => setLogPanelOpen(false)}>
                  {t('chats.logs.close')}
                </button>
              </div>
              <div className="chats-log-body chats-log-body--expandable">
                {loadingLogsList ? (
                  <p className="chats-log-muted">{t('chats.logs.loading')}</p>
                ) : logsList.length === 0 ? (
                  <p className="chats-log-muted">{t('chats.logs.emptyList')}</p>
                ) : (
                  <ul className="chats-log-list chats-log-list--expandable">
                    {logsList.map((g) => (
                      <ChatLogExpandableItem
                        key={g.id}
                        g={g}
                        isExpanded={selectedGraphId === g.id}
                        onToggle={() => handleSelectLog(g.id)}
                        logDetail={selectedGraphId === g.id ? messageLog : null}
                        loadingLog={selectedGraphId === g.id && loadingLog}
                        logError={selectedGraphId === g.id ? logError : null}
                        t={t}
                        lang={lang}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </aside>
          )}
        </div>
      )}
      </>
    </PageSection>
      <Modal
        open={addTestModalOpen}
        title={t('chats.addTestChat.modalTitle')}
        onClose={() => {
          if (!createTestLoading) {
            setAddTestModalOpen(false)
            setAddTestModalConfigId(null)
            setAddTestModalFanName('test_fan')
          }
        }}
        footer={
          <div className="modal-actions">
            <button
              type="button"
              className="modal-btn modal-btn-secondary"
              onClick={() => setAddTestModalOpen(false)}
              disabled={createTestLoading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="modal-btn modal-btn-primary"
              onClick={handleCreateTestChat}
              disabled={
                !addTestModalModelId ||
                (isTester && !addTestModalConfigId) ||
                !(addTestModalFanName || '').trim() ||
                createTestLoading
              }
            >
              {createTestLoading ? t('common.saving') : t('chats.addTestChat')}
            </button>
          </div>
        }
      >
        <div className="chats-add-modal-content">
          <div className="modal-form-field">
            <label className="chats-add-modal-label">{t('chats.addTestChat.fanName')}</label>
            <input
              type="text"
              className="chats-add-modal-input"
              value={addTestModalFanName}
              onChange={(e) => setAddTestModalFanName(e.target.value || '')}
              placeholder={t('chats.addTestChat.fanNamePlaceholder')}
            />
          </div>
          <div className="modal-form-field">
            <label className="chats-add-modal-label">{t('chats.filter.model')}</label>
            <Select
              className="chats-add-modal-select"
              value={addTestModalModelId != null ? String(addTestModalModelId) : ''}
              placeholder={t('chats.addTestChat.selectModel')}
              options={models.map((m) => ({ value: String(m.id), label: m.of_email || m.name || t('common.notAvailable') }))}
              onChange={(v) => setAddTestModalModelId(v ? Number(v) : null)}
              dropdownPosition="up"
            />
          </div>
          <div className="modal-form-field">
            <label className="chats-add-modal-label">{t('chats.addTestChat.config')}{isTester ? ' *' : ''}</label>
            <Select
              className="chats-add-modal-select"
              value={addTestModalConfigId != null ? String(addTestModalConfigId) : ''}
              placeholder={t('chats.addTestChat.selectConfig')}
              options={[
                ...(canManage ? [{ value: '', label: t('chats.addTestChat.configActive') }] : []),
                ...configsList.map((c) => ({
                  value: String(c.id),
                  label: `${c.name}${c.is_active ? ` (${t('chats.addTestChat.active')})` : ''}`,
                })),
              ]}
              onChange={(v) => setAddTestModalConfigId(v && v !== '' ? Number(v) : null)}
              dropdownPosition="up"
            />
          </div>
        </div>
      </Modal>
      <Modal
        open={editTestModalOpen}
        title={t('chats.editTestChat.title')}
        onClose={() => !editTestLoading && setEditTestModalOpen(false)}
        footer={
          <div className="modal-actions">
            <button
              type="button"
              className="modal-btn modal-btn-secondary"
              onClick={() => setEditTestModalOpen(false)}
              disabled={editTestLoading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="modal-btn modal-btn-primary"
              onClick={handleEditTestChat}
              disabled={!(editTestModalFanName || '').trim() || editTestLoading}
            >
              {editTestLoading ? t('common.saving') : t('common.save')}
            </button>
          </div>
        }
      >
        <div className="chats-add-modal-content">
          <div className="modal-form-field">
            <label className="chats-add-modal-label">{t('chats.addTestChat.fanName')}</label>
            <input
              type="text"
              className="chats-add-modal-input"
              value={editTestModalFanName}
              onChange={(e) => setEditTestModalFanName(e.target.value || '')}
              placeholder={t('chats.addTestChat.fanNamePlaceholder')}
            />
          </div>
          <div className="modal-form-field">
            <label className="chats-add-modal-label">{t('chats.filter.model')}</label>
            <div className="chats-edit-model-readonly">
              {selected && models.find((m) => m.id === selected.modelId)?.of_email || models.find((m) => m.id === selected?.modelId)?.name || t('chats.modelFallback', { id: selected?.modelId })}
            </div>
          </div>
          <div className="modal-form-field">
            <label className="chats-add-modal-label">{t('chats.addTestChat.config')}</label>
            <Select
              className="chats-add-modal-select"
              value={editTestModalConfigId}
              placeholder={t('chats.addTestChat.selectConfig')}
              options={[
                ...(canManage ? [{ value: '', label: t('chats.addTestChat.configActive') }] : []),
                ...configsList.map((c) => ({
                  value: String(c.id),
                  label: `${c.name}${c.is_active ? ` (${t('chats.addTestChat.active')})` : ''}`,
                })),
              ]}
              onChange={(v) => setEditTestModalConfigId(v || '')}
              dropdownPosition="up"
            />
          </div>
        </div>
      </Modal>
      <ConfirmModal
        open={Boolean(confirmAction)}
        title={
          confirmAction === 'delete'
            ? t('chats.delete.title')
            : confirmAction === 'unlock'
              ? t('chats.safetyLock.unlockTitle')
            : confirmAction === 'manual_on'
              ? t('chats.manual.enable.title')
              : t('chats.manual.disable.title')
        }
        message={
          confirmAction === 'delete'
            ? t('chats.delete.message')
            : confirmAction === 'unlock'
              ? t('chats.safetyLock.unlockMessage')
            : confirmAction === 'manual_on'
              ? t('chats.manual.enable.message')
              : t('chats.manual.disable.message')
        }
        confirmLabel={
          actionLoading
            ? (confirmAction === 'delete' ? t('common.deleting') : t('common.saving'))
            : (confirmAction === 'delete' ? t('common.delete') : t('common.save'))
        }
        danger={confirmAction === 'delete'}
        confirmDisabled={actionLoading}
        onCancel={() => {
          if (!actionLoading) setConfirmAction(null)
        }}
        onConfirm={handleConfirmAction}
      />
    </>
  )
}
